import React, { useState, useEffect } from 'react';
import { X, Gift } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const POPUP_DELAY = 17000; // 17 seconds

const DiscountPopup = () => {
  const [show, setShow] = useState(false);
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const dismissed = localStorage.getItem('discountPopupDismissed');
    if (dismissed) return;
    const timer = setTimeout(() => setShow(true), POPUP_DELAY);
    return () => clearTimeout(timer);
  }, []);

  const handleClose = () => {
    setShow(false);
    localStorage.setItem('discountPopupDismissed', Date.now().toString());
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await axios.post(`${API}/newsletter/signup`, { email });
      setSubmitted(true);
      localStorage.setItem('discountPopupDismissed', Date.now().toString());
      toast.success("You're in — keep an eye on your inbox for new drops & private sales.");
    } catch (err) {
      if (err.response?.data?.detail === 'Email already subscribed') {
        setSubmitted(true);
        localStorage.setItem('discountPopupDismissed', Date.now().toString());
      } else {
        toast.error('Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center px-4" data-testid="discount-popup-overlay">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={handleClose} />
      <div
        className="relative w-full max-w-md bg-[#0a0908] border border-[#D4AF37]/30 overflow-hidden"
        data-testid="discount-popup"
        style={{ animation: 'popupSlideIn 0.5s ease-out' }}
      >
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 z-10 text-white/40 hover:text-white/80 transition-colors"
          data-testid="discount-popup-close"
        >
          <X size={20} />
        </button>

        <div className="h-1 bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" />

        <div className="p-8 md:p-10">
          {!submitted ? (
            <>
              <div className="flex justify-center mb-5">
                <div className="w-16 h-16 border border-[#D4AF37]/30 flex items-center justify-center">
                  <Gift size={28} className="text-[#D4AF37]" />
                </div>
              </div>

              <div className="text-center mb-6">
                <p className="font-arabic text-xl text-[#D4AF37]/50 mb-1">انضم إلى عالمنا</p>
                <h3 className="font-heading text-2xl md:text-3xl tracking-wide text-white mb-2">
                  JOIN <span className="text-[#D4AF37]">THE LIST</span>
                </h3>
                <p className="font-body text-white/50 text-sm leading-relaxed">
                  Get early access to new fragrances, private sales, and restock alerts before anyone else.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-3">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                  data-testid="discount-popup-email"
                  className="w-full bg-[#050505] border border-[#D4AF37]/20 focus:border-[#D4AF37] text-white px-4 py-3.5 outline-none font-body text-sm placeholder:text-white/25"
                />
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="discount-popup-submit"
                  className="w-full bg-[#D4AF37] text-[#050505] py-3.5 font-heading text-xs tracking-[0.25em] hover:bg-[#E5C158] transition-colors disabled:opacity-50"
                >
                  {loading ? 'JOINING...' : 'JOIN THE LIST'}
                </button>
              </form>

              <p className="text-center mt-4">
                <button onClick={handleClose} className="font-body text-white/25 text-xs hover:text-white/40 transition-colors">
                  Maybe later
                </button>
              </p>
            </>
          ) : (
            <div className="text-center" data-testid="discount-popup-success">
              <div className="flex justify-center mb-5">
                <div className="w-16 h-16 border border-[#D4AF37]/30 flex items-center justify-center bg-[#D4AF37]/10">
                  <Gift size={28} className="text-[#D4AF37]" />
                </div>
              </div>

              <p className="font-arabic text-xl text-[#D4AF37]/50 mb-1">أهلاً بك</p>
              <h3 className="font-heading text-2xl tracking-wide text-white mb-2">
                WELCOME TO <span className="text-[#D4AF37]">QASR</span>
              </h3>
              <p className="font-body text-white/50 text-sm mb-6 leading-relaxed">
                You&apos;re on the list. We&apos;ll send you a note when new fragrances drop and when private sales begin.
              </p>

              <button
                onClick={handleClose}
                className="mt-2 font-heading text-[10px] tracking-[0.2em] text-[#D4AF37] border-b border-[#D4AF37]/30 pb-0.5 hover:border-[#D4AF37] transition-colors"
                data-testid="discount-popup-start-shopping"
              >
                START SHOPPING
              </button>
            </div>
          )}
        </div>

        <div className="h-px bg-gradient-to-r from-transparent via-[#D4AF37]/30 to-transparent" />
      </div>

      <style>{`
        @keyframes popupSlideIn {
          from { opacity: 0; transform: translateY(20px) scale(0.97); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </div>
  );
};

export default DiscountPopup;
